// Bulk edit: one set of changes (category, brand, supplier, unit, tags, shelf,
// active, VAT) applied to many products. Prices keep their number: fixing
// "prices include VAT" after an import changes what the customer pays, and the
// preview says so. Stock is never touched.
import { mulDivRound, type SyncOp } from '@ims/core'
import type { ProductRow } from '@ims/data'
import { parseTags } from './catalog.ts'
import { updateOp } from './rows.ts'

/** Tags are added to or taken off what each product has, never replaced. */
export type TagChange = { add: string[] } | { remove: string[] }

/** Fields left undefined are kept as they are. */
export interface BulkChange {
  price_includes_tax?: boolean
  /** null = no VAT. */
  tax_rate_id?: string | null
  /** null = no category. */
  category_id?: string | null
  /** Restaurant mode: on the menu (true) or an ingredient (false). */
  for_sale?: boolean
  /** null = no brand. */
  brand_id?: string | null
  /** The usual supplier; null = none. */
  supplier_id?: string | null
  /** null = no unit. */
  unit_id?: string | null
  /** null = no shelf written. */
  shelf_location?: string | null
  is_active?: boolean
  /** Restaurant mode: whose ticket an order of it prints on. */
  kitchen_station?: ProductRow['kitchen_station']
  /** The mark on the guest's menu; null = no mark. */
  diet?: ProductRow['diet']
  spicy?: boolean
  tags?: TagChange
}

export interface BulkPlan {
  /** Commits of at most `chunkSize` product updates; each one is one sync tx. */
  chunks: SyncOp[][]
  /** Products whose values change. */
  changed: ProductRow[]
  /** Products in the selection that already match. */
  unchanged: number
  /** Variants added because their group or a sibling was selected. */
  addedVariants: number
}

/** Fields set to one value on every product (tags are worked out per product). */
const FIELDS = [
  'price_includes_tax',
  'tax_rate_id',
  'category_id',
  'for_sale',
  'brand_id',
  'supplier_id',
  'unit_id',
  'shelf_location',
  'is_active',
  'kitchen_station',
  'diet',
  'spicy',
] as const

function tagList(change: TagChange): string[] {
  return parseTags(('add' in change ? change.add : change.remove).join(','))
}

export function isEmptyChange(change: BulkChange): boolean {
  return (
    FIELDS.every((f) => change[f] === undefined) &&
    (!change.tags || tagList(change.tags).length === 0)
  )
}

/** How many separate things the change sets (the dialog's "4 changes"). */
export function countChanges(change: BulkChange): number {
  return (
    FIELDS.filter((f) => change[f] !== undefined).length +
    (change.tags && tagList(change.tags).length > 0 ? 1 : 0)
  )
}

/**
 * A product's tags after the change, in the stored form ("otc, fever"; null
 * for none). Case does not make a second tag, and one already there keeps its
 * spelling.
 */
export function changedTags(current: string | null, change: TagChange): string | null {
  const have = parseTags(current)
  const wanted = tagList(change)
  const lower = (list: string[]) => new Set(list.map((t) => t.toLowerCase()))
  const next =
    'add' in change
      ? [...have, ...wanted.filter((t) => !lower(have).has(t.toLowerCase()))]
      : have.filter((t) => !lower(wanted).has(t.toLowerCase()))
  return next.join(', ') || null
}

/** How many of these products carry the tag. */
export function countWithTag(rows: readonly ProductRow[], tag: string): number {
  const wanted = tag.trim().toLowerCase()
  return rows.filter((p) => parseTags(p.tags).some((t) => t.toLowerCase() === wanted)).length
}

/**
 * Products whose unit would start or stop allowing parts ("0.5 kg") with the
 * new unit. Stock numbers are not converted, so the dialog says how many.
 */
export function unitDecimalChanges(
  rows: readonly ProductRow[],
  units: readonly { id: string; allows_decimal: boolean }[],
  unitId: string | null,
): { toDecimal: number; toWhole: number } {
  const decimal = (id: string | null) => units.find((u) => u.id === id)?.allows_decimal ?? false
  const after = decimal(unitId)
  let toDecimal = 0
  let toWhole = 0
  for (const p of rows) {
    if (p.unit_id === unitId || p.kind === 'group') continue
    const before = decimal(p.unit_id)
    if (!before && after) toDecimal += 1
    if (before && !after) toWhole += 1
  }
  return { toDecimal, toWhole }
}

/**
 * The selected products plus the rest of their variant families. VAT and
 * category are shared by a group and its variants (lib/variants.ts
 * SHARED_FIELDS), so changing one member alone would be undone by the next
 * group save.
 */
export function expandFamilies(
  selectedIds: Iterable<string>,
  all: readonly ProductRow[],
): { rows: ProductRow[]; addedVariants: number } {
  const byId = new Map(all.map((p) => [p.id, p]))
  const variantsOf = new Map<string, ProductRow[]>()
  for (const p of all) {
    if (p.parent_id) variantsOf.set(p.parent_id, [...(variantsOf.get(p.parent_id) ?? []), p])
  }
  const picked = new Set<string>()
  const direct = new Set<string>()
  const add = (p: ProductRow | undefined) => {
    if (!p || p.deleted_at) return
    picked.add(p.id)
  }
  for (const id of selectedIds) {
    const p = byId.get(id)
    if (!p) continue
    direct.add(id)
    const groupId = p.kind === 'group' ? p.id : p.parent_id
    if (groupId) {
      add(byId.get(groupId))
      for (const v of variantsOf.get(groupId) ?? []) add(v)
    } else {
      add(p)
    }
  }
  const rows = [...picked].map((id) => byId.get(id)!)
  const addedVariants = rows.filter((p) => p.kind === 'variant' && !direct.has(p.id)).length
  return { rows, addedVariants }
}

export function planBulkEdit(
  selectedIds: Iterable<string>,
  all: readonly ProductRow[],
  change: BulkChange,
  chunkSize = 200,
): BulkPlan {
  const { rows, addedVariants } = expandFamilies(selectedIds, all)
  const changed: ProductRow[] = []
  const ops: SyncOp[] = []
  for (const p of rows) {
    const diff: Record<string, unknown> = {}
    for (const f of FIELDS) {
      const value = change[f]
      if (value !== undefined && p[f] !== value) diff[f] = value
    }
    if (change.tags) {
      const tags = changedTags(p.tags, change.tags)
      if (tags !== (parseTags(p.tags).join(', ') || null)) diff.tags = tags
    }
    if (Object.keys(diff).length === 0) continue
    changed.push(p)
    ops.push(updateOp('products', p, diff))
  }
  const chunks: SyncOp[][] = []
  for (let i = 0; i < ops.length; i += chunkSize) chunks.push(ops.slice(i, i + chunkSize))
  return { chunks, changed, unchanged: rows.length - changed.length, addedVariants }
}

/** What the customer pays for one unit at this price (half away from zero). */
export function customerPays(pricePaisa: number, rateBp: number, includesVat: boolean): number {
  return includesVat ? pricePaisa : mulDivRound(pricePaisa, 10_000 + rateBp, 10_000)
}
